import type { FC, ReactNode } from "react";

import { clsx } from "@alpacahq/ui/old";

interface AddressTextAreaProps {
  id: string;
  label: string;
  required?: boolean;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  rightIcon?: ReactNode;
  errorMsg?: ReactNode;
  successMsg?: ReactNode;
  helpMsg?: ReactNode;
  className?: string;
  rows?: number;
}

const STATUS_TEXTAREA_CLASSES = {
  error:
    "border-rose-500 text-rose-500 placeholder-rose-500 placeholder-shown:border-rose-500 focus:border-gray-500 focus:text-gray-700",
  success:
    "border-emerald-500 text-gray-700 placeholder-gray-400 placeholder-shown:border-emerald-500 focus:border-gray-500",
  neutral:
    "border-gray-400 text-gray-700 placeholder-gray-400 placeholder-shown:border-gray-400 focus:border-gray-500",
} as const;

export const AddressTextArea: FC<AddressTextAreaProps> = ({
  id,
  label,
  required,
  value,
  placeholder,
  onChange,
  onBlur,
  rightIcon,
  errorMsg,
  successMsg,
  helpMsg,
  className,
  rows = 2,
}) => {
  const status: "error" | "success" | "neutral" = errorMsg
    ? "error"
    : successMsg
      ? "success"
      : "neutral";
  const message = errorMsg ?? (status === "success" ? successMsg : helpMsg);
  const hasMsg = !!errorMsg || !!successMsg || !!helpMsg;

  return (
    <div className="flex flex-col">
      <label
        htmlFor={id}
        className="mb-1 block font-normal text-gray-800 text-sm"
      >
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <textarea
          id={id}
          rows={rows}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          className={clsx(
            "block w-full resize-none rounded border bg-white px-3 py-2 font-medium text-sm leading-6 transition-colors focus:ring-0",
            rightIcon && "pr-9",
            STATUS_TEXTAREA_CLASSES[status],
            className,
          )}
        />
        {rightIcon && (
          <div className="absolute top-1/2 right-2 flex -translate-y-1/2 items-center justify-center text-gray-700">
            {rightIcon}
          </div>
        )}
      </div>
      {hasMsg && (
        <div
          className={clsx(
            "mt-1 font-normal text-xs",
            status === "error" && "text-rose-500",
            status === "success" && "text-emerald-500",
            status === "neutral" && "text-gray-500",
          )}
        >
          {message}
        </div>
      )}
    </div>
  );
};
